<template>
	<div class="editor-wrapper">
		<VueEditor
			class="editor"
			useCustomImageHandler
			:value="value"
			@input="$emit('input', $event)"
			@image-added="handleImageAdded"
			:editorOptions="editorSettings"
			:customModules="customModulesForEditor"
		/>
		<div
			class="preview"
			v-if="preview"
			@click.self="preview = false"
		>
			<div
				class="preview__content"
				v-html="value"
			></div>
		</div>
	</div>
</template>

<script>
import { VueEditor, Quill } from 'vue2-editor'
import ImageResize from 'quill-image-resize'
Quill.register('modules/imageResize', ImageResize)
Quill.register(Quill.import('attributors/style/align'), true)

const Size = Quill.import('attributors/style/size')
Size.whitelist = ['10px', '14px', '18px', '24px']
Quill.register(Size, true)

const icons = Quill.import('ui/icons')
icons['preview'] = `<svg enable-background="new 0 0 26 26" id="Слой_1" version="1.1" viewBox="0 0 26 26" xml:space="preserve" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"><g><path d="M24.8935547,12.5195313C22.2988281,8.1835938,18.6308594,6.25,13,6.25   S3.7011719,8.1835938,1.1064453,12.5195313C1.0161743,12.6702881,0.9967041,12.838501,1.0179443,13   c-0.0212402,0.161499-0.00177,0.3297119,0.088501,0.4804688C3.7011719,17.8164063,7.3691406,19.75,13,19.75   s9.2988281-1.9335938,11.8935547-6.2695313C24.9838257,13.3297119,25.0032959,13.161499,24.9820557,13   C25.0032959,12.838501,24.9838257,12.6702881,24.8935547,12.5195313z M13,17.5c-2.4814453,0-4.5-2.0185547-4.5-4.5   s2.0185547-4.5,4.5-4.5s4.5,2.0185547,4.5,4.5S15.4814453,17.5,13,17.5z M2.5728149,13   c1.3046875-2.0955811,3.2023315-4.1636963,7.0197144-4.9327393C8.0281982,9.1512451,7,10.9567871,7,13   s1.0281982,3.8487549,2.5925293,4.9327393C5.7751465,17.1636963,3.8775024,15.0955811,2.5728149,13z M16.4074707,17.9327393   C17.9718018,16.8487549,19,15.0432129,19,13s-1.0281982-3.8487549-2.5925293-4.9327393   C20.2248535,8.8363037,22.1224976,10.9044189,23.4271851,13C22.1224976,15.0955811,20.2248535,17.1636963,16.4074707,17.9327393z" fill="currentColor"/><path d="M15.1123047,12.1308594L15,12.1445313c-0.7480469,0-1.3701172-0.5888672-1.4150391-1.3408203   c-0.0126953-0.2001953-0.1044922-0.3876953-0.2558594-0.5195313c-0.1523438-0.1318359-0.3505859-0.1884766-0.5507813-0.1826172   C11.2666016,10.2177734,10.0830078,11.4912109,10.0830078,13c0,1.6083984,1.3085938,2.9169922,2.9169922,2.9169922   S15.9169922,14.6083984,15.9169922,13c0-0.0605469-0.0058594-0.1201172-0.0146484-0.1748047   C15.8720703,12.4169922,15.4892578,12.1064453,15.1123047,12.1308594z" fill="currentColor"/></g></svg>`

export default {
	props: ['value'],

	data () {
		return {
			preview: false,
			editorSettings: {
				modules: {
					imageResize: {},
					toolbar: {
						container: [
							[{ 'size': ['10px', '14px', '18px', '24px'] }],
							['bold', 'italic', 'underline', 'strike'],
							[{ 'align': [] }],
							[{ 'list': 'ordered' }, { 'list': 'bullet' }],
							[{ 'color': [] }],
							['link'],
							['clean'],
							['image'],
							['preview']
						],
						handlers: { 'preview': () => (this.preview = true) }
					}
				}
			},

			customModulesForEditor: [
				{ alias: 'imageResize', module: ImageResize }
			]
		}
	},

	methods: {
		handleImageAdded (file, Editor, cursorLocation, resetUploader) {
			this.$emit('image-added', { file, Editor, cursorLocation, resetUploader })
		}

		// handleImageRemoved (file) {
		// 	this.$emit('image-removed', file)
		// }
	},

	components: {
		VueEditor
	}
}
</script>

<style lang="scss" scoped>
.editor-wrapper {
	width: 100%;
	height: 100%;
}

.editor {
	width: 100%;
	max-width: 800px;
	height: 100%;

	/deep/ .ql-tooltip {
		position: absolute !important;
		top: unset !important;
		left: unset !important;
		margin: 0 !important;
		bottom: 20px !important;
		right: 10px !important;
	}

	/deep/ .ql-container {
		overflow: hidden;
		word-break: break-word !important;
		position: static !important;
	}

	/deep/
		.ql-snow
		.ql-picker-options
		.ql-picker-item[data-value="10px"]::before {
		content: "Small" !important;
		font-size: 10px !important;
	}

	/deep/
		.ql-snow
		.ql-picker-options
		.ql-picker-item[data-value="14px"]::before {
		content: "Normal";
		font-size: 14px !important;
	}

	/deep/
		.ql-snow
		.ql-picker-options
		.ql-picker-item[data-value="18px"]::before {
		content: "Large";
		font-size: 18px !important;
	}

	/deep/
		.ql-snow
		.ql-picker-options
		.ql-picker-item[data-value="24px"]::before {
		content: "Huge";
		font-size: 24px !important;
	}

	/deep/ .ql-formats {
		margin-right: 5px !important;
	}
	/deep/ .ql-formats > * {
		transform: scale(0.8);
	}
}

.preview {
	position: fixed;
	top: 0;
	right: 0;
	left: 0;
	bottom: 0;
	width: 100%;
	height: 100%;
	background-color: #00000060;
	display: flex;
	align-items: flex-start;
	justify-content: center;
	padding: 20px;
	overflow-y: auto;
	z-index: 999;

	&__content {
		width: 800px;
		min-height: 80%;
		padding: 20px;
		background-color: #fff;
		box-shadow: 0 1px 3px #0000000a;
		border-radius: 3px;
		word-break: break-all;

		img {
			max-width: 100%;
		}
	}
}
</style>
